import { Location } from 'history';
import { CurrentUserService } from '../gs-core/client/auth';
import { Organization, Permission, User } from '../gs-core/common/models';
import {
  Doc,
  DocProxy, DocStatus, EDocsPermissions, FilterParamsFull, FilterParamsFullModel, FilterParamsModel,
  PermissionEvaluator,
  SubjectType
} from '../common/models';
import { SubjectSelection, SubjectService } from './SubjectService';
import { PrmService } from '../gs-core/common/services';

export interface CommonState {
  filters: FilterParamsFull;
  currentUser?: User;
  permissions: Permission[];
  selection?: SubjectSelection;
  doc?: Doc;
  canUpload: boolean;
  canEdit: boolean;
}

const EDIT_DOCUMENTS = EDocsPermissions.EDIT_DOCUMENTS;

export class CommonDocLoader {
  static createState(
    location: Location,
    currentUser?: User,
    permissions: Permission[] = [],
    selection?: SubjectSelection,
    owningUnit?: Organization,
    doc?: Doc
  ): CommonState {
    const { page, pageSize, createdById, createdBefore = new Date().getTime(), title, text, statuses } =
      FilterParamsModel.createFromQuery(location.search);
    let canUpload = false;
    let canEdit = false;
    const subject = (doc && doc.subject) || (selection && selection.selectedSubject);
    const { pathname } = location;
    const createdBy = pathname === '/' || createdById ? currentUser : undefined;
    owningUnit = (doc && doc.owningUnit) || owningUnit;

    if (currentUser) {
      const permissionEvaluator = PermissionEvaluator.forUser(currentUser)
        .withPermissions(permissions);

      if (subject) {
        const owningUnits = permissionEvaluator.forDoc({ subject }).findOwningUnitsOverlap();
        const docProxies = owningUnits.map(unit => ({ subject, owningUnit: unit }) as DocProxy);

        canUpload = docProxies
          .some(docProxy => permissionEvaluator.forDoc(docProxy).canEdit());

        canEdit = !!doc && permissionEvaluator.forDoc(doc).canEdit();
      } else if (owningUnit) {
        canEdit = permissionEvaluator.forDoc({ owningUnit }).canEdit();
        canUpload = canEdit;
      } else {
        canUpload = permissions.some(permission => permission.code === EDIT_DOCUMENTS);
      }
    }

    return {
      filters: FilterParamsFullModel.create({
        subject,
        title,
        text,
        owningUnits: owningUnit ? [ owningUnit ] : undefined,
        createdBy,
        createdBefore,
        statuses: (statuses as DocStatus[]),
        pageSize,
        page
      }),
      currentUser,
      permissions,
      selection,
      doc,
      canUpload,
      canEdit
    };
  }

  constructor(
    private currentUserService: CurrentUserService,
    private prmService: PrmService,
    private subjectService: SubjectService
  ) {}

  async loadInitialState(pathProps: any, location: Location, doc?: Doc) {
    const currentUser = await this.currentUserService.find();
    const permissions = await this.currentUserService.findPermissions();

    if (!currentUser) {
      throw new Error('Unable to find current user');
    }

    const filters = FilterParamsModel.create(pathProps);

    const subjectSelection = (filters.subjectType && filters.subjectUserId) ?
      await this.subjectService.findSelection(filters.subjectUserId, filters.subjectType as SubjectType) :
      undefined;

    const owningUnit = filters.owningUnitIds && filters.owningUnitIds.length ?
      await this.prmService.findUser(filters.owningUnitIds[0]) as Organization :
      undefined;

    return CommonDocLoader.createState(
      location,
      currentUser,
      permissions,
      subjectSelection,
      owningUnit,
      doc
    );
  }
}



// WEBPACK FOOTER //
// ./src/services/CommonDocLoader.ts